import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  MenuItem,
  Paper,
  Stack,
  Switch,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  CheckCircle,
  Close as CloseIcon,
  CreditCardOutlined,
  DescriptionOutlined,
  InfoOutlined,
  KeyboardArrowLeft,
  PersonOutlineOutlined,
} from "@mui/icons-material";
import { gql, request } from "graphql-request";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { SyntheticEvent } from "react";
import { useUserContext, useUserContextDispatch } from "../../hooks/userContext";
import { useDesignerApplicationContext } from "../../hooks/designer-application-context";
import { useNotification } from "../../contexts/NotificationContext";
import { useHighLevelContext } from "../../hooks/HighLevelContext";
import type { Plan } from "../../hooks/HighLevelContext/types";
import {
  DesignerApplicationSteps,
  PaymentFrequency,
  States,
} from "../../__generated__/types";
import { SubscriptionCard } from "../../components/subscription-card";
import {
  DESIGNER_SMART_GUILD_ANNUAL_FEE_DEFAULT,
  DESIGNER_SMART_GUILD_MONTHLY_FEE_DEFAULT,
  DESIGNER_SMART_GUILD_TOTAL_ANNUAL_FEE_DEFAULT,
  getDesignerSmartGuildDisplayedFeatures,
} from "../../utils/designer-smart-guild-pricing";
import { resolveDesignerSmartGuildPresentation } from "./designer-smart-guild-presentation-utils";
import { resolveMembershipStatusAfterMutation } from "./membership-status-utils";
import {
  calculateAnnualTermEndDateFromStartDate,
  countRemainingMonthlyCyclesInAnnualTerm,
  resolveDesignerCurrentPlanButtonState,
} from "./subscription-action-utils";
import "./styles.scss";

type AccountTabValue =
  | "profile"
  | "billing"
  | "subscription";

type BillingCadence = "bi-annually" | "annually" | "monthly";
type DesignerPlanTier = "Home" | "Design" | "Design+";
type HomeownerPlanTier = "Basic" | "Guild" | "Guild+";
type PlanTier = DesignerPlanTier | HomeownerPlanTier;

type HostedFlowMode = "add" | "edit";

interface PaymentMethodItem {
  id: string;
  brand: string;
  last4: string;
  expiresAt: string;
}

interface ProfileFormState {
  firstName: string;
  lastName: string;
  phoneNumber: string;
  email: string;
  businessName: string;
  mailingAddress: string;
  city: string;
  businessState: string;
  zipCode: string;
  businessWebsite: string;
}

interface PasswordFormState {
  currentPassword: string;
  newPassword: string;
}

interface HostedCheckoutRequest {
  mode: HostedFlowMode;
  heading: string;
  completionMessage: string;
}

interface DesignerPlanDefinition {
  tier: DesignerPlanTier;
  plansByCadence: Partial<Record<BillingCadence, Plan>>;
}

interface HomeownerPlanDefinition {
  tier: HomeownerPlanTier;
  plansByCadence: Partial<Record<BillingCadence, Plan>>;
}

interface GetAcceptCustomerProfileTokenResponse {
  getAcceptCustomerProfileToken: string;
}

interface GetAcceptCustomerProfileTokenVariables {
  userId: string;
  email: string;
  customerInfo: {
    firstName: string;
    lastName: string;
    address: string;
    city: string;
    state: string;
    zipCode: string;
  };
}

interface GetAuthorizeNetPaymentMethodsResponse {
  getAuthorizeNetPaymentMethods: PaymentMethodItem[];
}

interface GetAuthorizeNetPaymentMethodsVariables {
  userId: string;
}

interface AccountMembershipStatus {
  userId: string;
  accountType?: string | null;
  subscription?: string | null;
  paymentStatus?: string | null;
  autoRenew?: boolean | null;
  billingCycle?: string | null;
  currentPlanTier?: string | null;
  nextRenewalDate?: string | null;
  subscriptionStartDate?: string | null;
  subscriptionEndDate?: string | null;
  monthlyPlatformFeeNextBillingDate?: string | null;
  latestTransactionId?: string | null;
}

interface GetAccountMembershipStatusResponse {
  getAccountMembershipStatus?: AccountMembershipStatus | null;
}

interface GetAccountMembershipStatusVariables {
  userId: string;
}

interface UpdateAccountMembershipResponse {
  updateAccountMembership?: AccountMembershipStatus | null;
}

interface UpdateAccountMembershipVariables {
  userId: string;
  subscription?: string | null;
  billingCycle?: string | null;
  autoRenew?: boolean | null;
  cancelAtPeriodEnd?: boolean | null;
}

type RecurringSubscriptionOperation = "create" | "upgrade" | "cancel";

interface RecurringSubscriptionResponse {
  success?: boolean;
  error?: string;
  message?: string;
  status?: string;
  operation?: string;
  subscriptionId?: string;
  membership?: AccountMembershipStatus | null;
}

const GET_ACCEPT_CUSTOMER_PROFILE_TOKEN_MUTATION = gql`
  mutation GetAcceptCustomerProfileToken(
    $userId: ID!
    $email: String!
    $customerInfo: CustomerInfoInput!
  ) {
    getAcceptCustomerProfileToken(
      userId: $userId
      email: $email
      customerInfo: $customerInfo
    )
  }
`;

const GET_AUTHORIZE_NET_PAYMENT_METHODS_QUERY = gql`
  query GetAuthorizeNetPaymentMethods($userId: ID!) {
    getAuthorizeNetPaymentMethods(userId: $userId) {
      id
      brand
      last4
      expiresAt
    }
  }
`;

const GET_ACCOUNT_MEMBERSHIP_STATUS_QUERY = gql`
  query GetAccountMembershipStatus($userId: ID!) {
    getAccountMembershipStatus(userId: $userId) {
      userId
      accountType
      subscription
      paymentStatus
      autoRenew
      billingCycle
      currentPlanTier
      nextRenewalDate
      subscriptionStartDate
      subscriptionEndDate
      monthlyPlatformFeeNextBillingDate
      latestTransactionId
    }
  }
`;

const UPDATE_ACCOUNT_MEMBERSHIP_MUTATION = gql`
  mutation UpdateAccountMembership(
    $userId: ID!
    $subscription: String
    $billingCycle: String
    $autoRenew: Boolean
    $cancelAtPeriodEnd: Boolean
  ) {
    updateAccountMembership(
      userId: $userId
      subscription: $subscription
      billingCycle: $billingCycle
      autoRenew: $autoRenew
      cancelAtPeriodEnd: $cancelAtPeriodEnd
    ) {
      userId
      accountType
      subscription
      paymentStatus
      autoRenew
      billingCycle
      currentPlanTier
      nextRenewalDate
      subscriptionStartDate
      subscriptionEndDate
      monthlyPlatformFeeNextBillingDate
      latestTransactionId
    }
  }
`;

const DEFAULT_PAYMENT_METHODS: PaymentMethodItem[] = [];

const DESIGNER_PLAN_TIER_ORDER: DesignerPlanTier[] = ["Design"];
const HOMEOWNER_PLAN_TIER_ORDER: HomeownerPlanTier[] = ["Basic", "Guild", "Guild+"];

const DESIGNER_PLAN_TIER_RANK: Record<DesignerPlanTier, number> = {
  Home: 1,
  Design: 2,
  "Design+": 3,
};

const HOMEOWNER_PLAN_TIER_RANK: Record<HomeownerPlanTier, number> = {
  Basic: 1,
  Guild: 2,
  "Guild+": 3,
};

const CADENCE_LABEL: Record<BillingCadence, string> = {
  "bi-annually": "Bi-Annually",
  annually: "Annually",
  monthly: "Monthly",
};

const createEmptyDesignerPlans = (): Record<DesignerPlanTier, DesignerPlanDefinition> => ({
  Home: {
    tier: "Home",
    plansByCadence: {},
  },
  Design: {
    tier: "Design",
    plansByCadence: {},
  },
  "Design+": {
    tier: "Design+",
    plansByCadence: {},
  },
});

const createEmptyHomeownerPlans = (): Record<HomeownerPlanTier, HomeownerPlanDefinition> => ({
  Basic: {
    tier: "Basic",
    plansByCadence: {},
  },
  Guild: {
    tier: "Guild",
    plansByCadence: {},
  },
  "Guild+": {
    tier: "Guild+",
    plansByCadence: {},
  },
});

const CANCELLATION_REASONS = [
  "I no longer need it",
  "I need more features",
  "Ease of use was less than expected",
  "I found another alternative",
  "It's too expensive",
  "Customer service was less than expected",
];

const AUTHORIZE_NET_CUSTOMER_PROFILE_ENDPOINT =
  process.env.REACT_APP_AUTHORIZE_NET_ENVIRONMENT?.toLowerCase() === "production"
    ? "https://accept.authorize.net/customer/manage"
    : "https://test.authorize.net/customer/manage";

const GRAPHQL_ENDPOINT =
  process.env.REACT_APP_GRAPHQL_URL ||
  (process.env.NODE_ENV === "development" ? "http://localhost:8080/graphql" : "/api/graphql");

const TERMS_OF_SERVICE_URL = "https://homedesignguild.com/terms-of-service";
const PRIVACY_POLICY_URL = "https://homedesignguild.com/privacy-policy";
const ENHANCIFY_PROMO_URL =
  "https://www.enhancify.com/home-design-guild-2-promo";

const resolveApiBaseUrl = (graphqlEndpoint: string): string => {
  const explicitApiUrl = String(process.env.REACT_APP_API_URL ?? "").trim();
  if (explicitApiUrl) {
    return explicitApiUrl.replace(/\/$/, "");
  }

  const normalizedGraphqlEndpoint = String(graphqlEndpoint ?? "").trim();
  if (!normalizedGraphqlEndpoint) {
    return process.env.NODE_ENV === "development" ? "http://localhost:8080" : "";
  }

  if (normalizedGraphqlEndpoint.startsWith("http://") || normalizedGraphqlEndpoint.startsWith("https://")) {
    try {
      const parsed = new URL(normalizedGraphqlEndpoint);
      const normalizedPath = parsed.pathname.replace(/\/graphql\/?$/, "");
      const joinedPath = normalizedPath.replace(/\/$/, "");
      return `${parsed.origin}${joinedPath}`;
    } catch (error) {
      return process.env.NODE_ENV === "development" ? "http://localhost:8080" : "";
    }
  }

  return process.env.NODE_ENV === "development" ? "http://localhost:8080" : "";
};

const API_BASE_URL = resolveApiBaseUrl(GRAPHQL_ENDPOINT);

const buildApiUrl = (path: string): string =>
  API_BASE_URL ? `${API_BASE_URL}${path}` : path;

const asString = (value: unknown): string | null => {
  if (value == null) {
    return null;
  }
  const normalized = String(value).trim();
  return normalized.length > 0 ? normalized : null;
};

const isCadenceValue = (value: string): value is BillingCadence =>
  value === "bi-annually" || value === "annually" || value === "monthly";

const getPaymentFrequencyFromCadence = (cadence: BillingCadence): PaymentFrequency => {
  if (cadence === "bi-annually") {
    return PaymentFrequency.BiAnnually;
  }

  if (cadence === "annually") {
    return PaymentFrequency.Annually;
  }

  return PaymentFrequency.Monthly;
};

const getPlanPriceValue = (plan: Plan | undefined): number => {
  const rawPrice = plan?.pricing?.price;
  if (typeof rawPrice === "number" && !Number.isNaN(rawPrice)) {
    return rawPrice;
  }

  if (typeof rawPrice === "string") {
    const parsedPrice = Number(rawPrice);
    return Number.isNaN(parsedPrice) ? 0 : parsedPrice;
  }

  return 0;
};

const getPlanOriginalPriceValue = (plan: Plan | undefined): number | null => {
  const rawOriginalPrice = plan?.pricing?.originalPrice;
  if (typeof rawOriginalPrice === "number" && !Number.isNaN(rawOriginalPrice)) {
    return rawOriginalPrice;
  }

  if (typeof rawOriginalPrice === "string") {
    const parsedOriginalPrice = Number(rawOriginalPrice);
    return Number.isNaN(parsedOriginalPrice) ? null : parsedOriginalPrice;
  }

  return null;
};

const normalizeSubscriptionValue = (rawValue: unknown): string => {
  return String(rawValue ?? "")
    .trim()
    .toLowerCase()
    .replace(/[\s_]+/g, "");
};

const inferPlanTierAndCadence = (
  rawSubscriptionValue: unknown
): {
  tier: DesignerPlanTier | null;
  cadence: BillingCadence | null;
} => {
  const normalized = normalizeSubscriptionValue(rawSubscriptionValue);

  const tier: DesignerPlanTier | null =
    normalized.includes("designplus") ||
    normalized.includes("design+") ||
    normalized.includes("guildplus") ||
    normalized.includes("guild+")
      ? "Design+"
      : normalized.includes("design") || normalized.includes("guild")
        ? "Design"
        : normalized.includes("home") ||
            normalized.includes("basic") ||
            normalized.includes("stylefile") ||
            normalized.includes("free")
          ? "Home"
          : null;

  const cadenceFromToken: BillingCadence | null = normalized.includes("monthly")
    ? "monthly"
    : normalized.includes("biannually") || normalized.includes("bi-annually")
      ? "bi-annually"
      : normalized.includes("annually") || normalized.includes("annual") || normalized.includes("year")
        ? "annually"
        : null;

  const cadence: BillingCadence | null = cadenceFromToken ?? (tier ? "annually" : null);

  return { tier, cadence };
};

const normalizeMembershipCadence = (
  rawValue: unknown,
  fallback: BillingCadence = "annually"
): BillingCadence => {
  const normalized = String(rawValue ?? "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]/g, "");

  if (!normalized) {
    return fallback;
  }

  if (normalized.includes("biannual") || normalized.includes("semiannual") || normalized.includes("6month")) {
    return "bi-annually";
  }

  if (normalized.includes("month")) {
    return "monthly";
  }

  if (normalized.includes("annual") || normalized.includes("year")) {
    return "annually";
  }

  return fallback;
};

const normalizeDesignerTier = (value: unknown): DesignerPlanTier | null => {
  const normalized = String(value ?? "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, "");

  if (!normalized) {
    return null;
  }

  if (
    normalized.includes("design+") ||
    normalized.includes("designplus") ||
    normalized.includes("guild+") ||
    normalized.includes("guildplus")
  ) {
    return "Design+";
  }

  if (
    normalized.includes("home") ||
    normalized.includes("basic") ||
    normalized.includes("stylefile") ||
    normalized.includes("free")
  ) {
    return "Home";
  }

  if (normalized.includes("design") || normalized.includes("guild")) {
    return "Design";
  }

  return null;
};

const normalizeHomeownerTier = (value: unknown): HomeownerPlanTier | null => {
  const normalized = String(value ?? "")
    .trim()
    .toLowerCase()
    .replace(/[\s_]+/g, "");

  if (!normalized) {
    return null;
  }

  if (normalized.includes("guildplus") || normalized.includes("guild+")) {
    return "Guild+";
  }

  if (normalized.includes("guild")) {
    return "Guild";
  }

  if (
    normalized.includes("basic") ||
    normalized.includes("stylefile") ||
    normalized.includes("free") ||
    normalized.includes("home")
  ) {
    return "Basic";
  }

  return null;
};

const getDesignerSubscriptionValue = (
  tier: DesignerPlanTier,
  cadence: BillingCadence
): string => {
  if (tier === "Home") {
    if (cadence === "monthly") return "HomeMonthly";
    if (cadence === "bi-annually") return "HomeBiAnnually";
    return "Home";
  }

  if (tier === "Design+") {
    if (cadence === "monthly") return "DesignPlusMonthly";
    if (cadence === "bi-annually") return "DesignPlusBiAnnually";
    return "DesignPlus";
  }

  if (cadence === "monthly") return "DesignMonthly";
  if (cadence === "bi-annually") return "DesignBiAnnually";
  return "Design";
};

const getHomeownerSubscriptionValue = (
  tier: HomeownerPlanTier,
  cadence: BillingCadence
): string => {
  if (tier === "Guild+") {
    return "GuildPlus";
  }

  if (tier === "Guild") {
    return cadence === "monthly" ? "GuildMonthly" : "GuildAnnually";
  }

  return "basic";
};

const getPreferredHomeownerCadences = (tier: HomeownerPlanTier): BillingCadence[] => {
  if (tier === "Guild+") {
    return ["annually", "monthly", "bi-annually"];
  }

  if (tier === "Guild") {
    return ["monthly", "annually", "bi-annually"];
  }

  return ["monthly", "annually", "bi-annually"];
};

const isFreeSubscriptionValue = (subscriptionValue: string): boolean => {
  const normalized = String(subscriptionValue ?? "")
    .trim()
    .toLowerCase();

  return normalized === "basic" || normalized === "free";
};

const inferTierFromPlan = (plan: Plan): DesignerPlanTier | null => {
  const explicitAudience = String(plan?.metadata?.audience ?? "").trim().toLowerCase();
  if (explicitAudience && explicitAudience !== "designer") {
    return null;
  }

  const lookup = `${plan.name} ${plan.slug ?? ""} ${String(plan.metadata?.tier ?? "")}`
    .toLowerCase()
    .replace(/\s+/g, "");

  if (!lookup || lookup.includes("homeowner")) {
    return null;
  }

  // New Smart Guild designer memberships should map into the current
  // designer tier model on My Account (Design / Design+ / Home).
  if (lookup.includes("guild+") || lookup.includes("guildplus")) {
    return "Design+";
  }

  if (lookup.includes("guild")) {
    return "Design";
  }

  if (lookup.includes("design+") || lookup.includes("designplus")) {
    return "Design+";
  }

  if (lookup.includes("home") && !lookup.includes("homeowner")) {
    return "Home";
  }

  if (lookup.includes("design")) {
    return "Design";
  }

  return null;
};

const inferHomeownerTierFromPlan = (plan: Plan): HomeownerPlanTier | null => {
  const explicitAudience = String(plan?.metadata?.audience ?? "").trim().toLowerCase();
  if (explicitAudience && explicitAudience !== "homeowner") {
    return null;
  }

  const lookup = `${String(plan.metadata?.tier ?? "")} ${plan.name} ${plan.slug ?? ""}`
    .toLowerCase()
    .replace(/[\s_]+/g, "");

  if (!lookup) {
    return null;
  }

  if (lookup.includes("guildplus") || lookup.includes("guild+")) {
    return "Guild+";
  }

  if (lookup.includes("guild")) {
    return "Guild";
  }

  if (
    lookup.includes("basic") ||
    lookup.includes("stylefile") ||
    lookup.includes("homeowner")
  ) {
    return "Basic";
  }

  return null;
};

const inferHomeownerTierAndCadence = (
  rawSubscriptionValue: unknown
): {
  tier: HomeownerPlanTier | null;
  cadence: BillingCadence | null;
} => {
  const normalized = normalizeSubscriptionValue(rawSubscriptionValue);

  const tier: HomeownerPlanTier | null = normalized.includes("guildplus") || normalized.includes("guild+")
    ? "Guild+"
    : normalized.includes("guild")
      ? "Guild"
      : normalized.includes("basic") ||
          normalized.includes("stylefile") ||
          normalized.includes("free") ||
          normalized.includes("home")
        ? "Basic"
        : null;

  const cadenceFromToken: BillingCadence | null = normalized.includes("monthly")
    ? "monthly"
    : normalized.includes("biannually") || normalized.includes("bi-annually")
      ? "bi-annually"
      : normalized.includes("annually") || normalized.includes("annual") || normalized.includes("year")
        ? "annually"
        : null;

  const defaultCadence: BillingCadence = tier === "Guild+" ? "annually" : "monthly";

  return {
    tier,
    cadence: cadenceFromToken ?? (tier ? defaultCadence : null),
  };
};

const mergeDesignerPlansFromHighLevel = (plans: Plan[]) => {
  const merged = createEmptyDesignerPlans();

  plans.forEach((plan) => {
    const audience = String(plan?.metadata?.audience ?? "").toLowerCase();
    if (audience && audience !== "designer") {
      return;
    }

    const tier = inferTierFromPlan(plan);
    if (!tier) {
      return;
    }

    const period = String(plan.pricing?.period ?? "").toLowerCase();
    if (!isCadenceValue(period)) {
      return;
    }

    merged[tier].plansByCadence[period] = plan;
  });

  return merged;
};

const mergeHomeownerPlansFromHighLevel = (plans: Plan[]) => {
  const merged = createEmptyHomeownerPlans();

  plans.forEach((plan) => {
    const audience = String(plan?.metadata?.audience ?? "").toLowerCase();
    if (audience && audience !== "homeowner") {
      return;
    }

    const tier = inferHomeownerTierFromPlan(plan);
    if (!tier) {
      return;
    }

    const period = String(plan.pricing?.period ?? "").toLowerCase();
    if (!isCadenceValue(period)) {
      return;
    }

    merged[tier].plansByCadence[period] = plan;
  });

  return merged;
};

const isDesignerPlanTier = (value: PlanTier): value is DesignerPlanTier =>
  value === "Home" || value === "Design" || value === "Design+";

const isHomeownerPlanTier = (value: PlanTier): value is HomeownerPlanTier =>
  value === "Basic" || value === "Guild" || value === "Guild+";

const formatCurrency = (value: number) =>
  new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);

const formatWholeDollarCurrency = (value: number) =>
  new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);

const parseDateValue = (value: unknown): Date | null => {
  if (!value) {
    return null;
  }

  const parsed = new Date(String(value));
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const mapUserToProfileForm = (user: Record<string, unknown> | null | undefined): ProfileFormState => {
  return {
    firstName: String(user?.firstName ?? ""),
    lastName: String(user?.lastName ?? ""),
    phoneNumber: String(user?.phoneNumber ?? ""),
    email: String(user?.email ?? ""),
    businessName: String(user?.businessName ?? ""),
    mailingAddress: String(user?.businessAddress ?? user?.address ?? ""),
    city: String(user?.businessCity ?? user?.city ?? ""),
    businessState: String(user?.businessState ?? user?.state ?? ""),
    zipCode: String(user?.businessZipCode ?? user?.zipCode ?? ""),
    businessWebsite: String(user?.businessWebsite ?? ""),
  };
};

export const MyAccountPage = () => {
  const { user, loading } = useUserContext();
  const { application } = useDesignerApplicationContext();
  const {
    updateDesignerAccountData,
    updateHomeownerAccountData,
    resetUserPassword,
    syncMembershipStatus,
  } =
    useUserContextDispatch();
  const { showError, showSuccess } = useNotification();
  const { getPlanList } = useHighLevelContext();

  const [activeTab, setActiveTab] = useState<AccountTabValue>("profile");
  const [profileForm, setProfileForm] = useState<ProfileFormState>(
    mapUserToProfileForm((user as Record<string, unknown> | null) ?? null)
  );
  const [passwordForm, setPasswordForm] = useState<PasswordFormState>({
    currentPassword: "",
    newPassword: "",
  });
  const [isSavingProfile, setIsSavingProfile] = useState(false);
  const [isSavingPassword, setIsSavingPassword] = useState(false);

  const [paymentMethods, setPaymentMethods] = useState<PaymentMethodItem[]>(
    DEFAULT_PAYMENT_METHODS
  );
  const [isPaymentMethodsLoading, setIsPaymentMethodsLoading] = useState(false);
  const [paymentMethodsError, setPaymentMethodsError] = useState<string | null>(null);
  const [billingMode, setBillingMode] = useState<"list" | "hosted">("list");
  const [billingRequest, setBillingRequest] = useState<HostedCheckoutRequest | null>(null);
  const [hostedToken, setHostedToken] = useState("");
  const [isHostedLoading, setIsHostedLoading] = useState(false);
  const [hostedError, setHostedError] = useState<string | null>(null);
  const [hostedFrameKey, setHostedFrameKey] = useState(0);
  const hostedFormRef = useRef<HTMLFormElement | null>(null);

  const [designerPlans, setDesignerPlans] = useState<Record<DesignerPlanTier, DesignerPlanDefinition>>(
    createEmptyDesignerPlans()
  );
  const [homeownerPlans, setHomeownerPlans] = useState<Record<HomeownerPlanTier, HomeownerPlanDefinition>>(
    createEmptyHomeownerPlans()
  );
  const [isPlansLoading, setIsPlansLoading] = useState(false);
  const [membershipStatus, setMembershipStatus] = useState<AccountMembershipStatus | null>(null);
  const [isMembershipStatusLoading, setIsMembershipStatusLoading] = useState(false);
  const [isMembershipUpdateLoading, setIsMembershipUpdateLoading] = useState(false);
  const [selectedCadence, setSelectedCadence] = useState<BillingCadence>("annually");
  const [currentPlanCadence, setCurrentPlanCadence] = useState<BillingCadence>("annually");
  const [currentPlanTier, setCurrentPlanTier] = useState<PlanTier>("Home");
  const [autoRenew, setAutoRenew] = useState(() => {
    const initialUser = (user as Record<string, unknown> | null | undefined) ?? null;
    if (typeof initialUser?.membershipAutoRenew === "boolean") {
      return initialUser.membershipAutoRenew;
    }

    return String(initialUser?.paymentStatus ?? "").trim().toLowerCase() !== "cancelled";
  });

  const [upgradeDialogOpen, setUpgradeDialogOpen] = useState(false);
  const [downgradeDialogOpen, setDowngradeDialogOpen] = useState(false);
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [feedbackDialogOpen, setFeedbackDialogOpen] = useState(false);
  const [addPaymentMethodPromptOpen, setAddPaymentMethodPromptOpen] = useState(false);
  const [upgradeConsentChecked, setUpgradeConsentChecked] = useState(true);
  const [pendingPlanTier, setPendingPlanTier] = useState<PlanTier | null>(null);
  const [pendingPlanCadence, setPendingPlanCadence] = useState<BillingCadence | null>(null);

  const [feedbackReasons, setFeedbackReasons] = useState<string[]>([]);
  const [feedbackNotes, setFeedbackNotes] = useState("");
  const syncMembershipStatusRef = useRef(syncMembershipStatus);

  const isDesignerAccount = (user?.accountType ?? "").toLowerCase() === "designer";
  const isHomeownerAccount = (user?.accountType ?? "").toLowerCase() === "homeowner";
  const canEditAccountDetails = isDesignerAccount || isHomeownerAccount;

  const stateOptions = useMemo(() => Object.values(States), []);

  const todayLabel = useMemo(
    () =>
      new Intl.DateTimeFormat("en-US", {
        weekday: "short",
        day: "2-digit",
        month: "long",
        year: "numeric",
      }).format(new Date()),
    []
  );

  const userSubscriptionDetails = (user as Record<string, unknown> | null | undefined)
    ?.subscriptionDetails as
    | {
        planType?: string;
        billingCycle?: string;
        startDate?: string;
        endDate?: string;
        annualTermStartDate?: string;
        annualTermEndDate?: string;
        monthlyPlatformFeeNextBillingDate?: string;
      }
    | undefined;
  const userMembershipNextRenewalDate = (user as Record<string, unknown> | null | undefined)
    ?.membershipNextRenewalDate;
  const persistedUserMembershipAutoRenew =
    typeof (user as Record<string, unknown> | null | undefined)?.membershipAutoRenew ===
    "boolean"
      ? Boolean((user as Record<string, unknown> | null | undefined)?.membershipAutoRenew)
      : null;
  const persistedUserPaymentStatus = String(
    (user as Record<string, unknown> | null | undefined)?.paymentStatus ?? ""
  )
    .trim()
    .toLowerCase();

  const designerMembershipStartDate = useMemo(
    () =>
      parseDateValue(
        membershipStatus?.subscriptionStartDate ??
          userSubscriptionDetails?.annualTermStartDate ??
          userSubscriptionDetails?.startDate ??
          null
      ),
    [
      membershipStatus?.subscriptionStartDate,
      userSubscriptionDetails?.annualTermStartDate,
      userSubscriptionDetails?.startDate,
    ]
  );

  const designerMembershipEndDate = useMemo(
    () => {
      const explicitEndDate = parseDateValue(
        membershipStatus?.subscriptionEndDate ??
          membershipStatus?.nextRenewalDate ??
          userSubscriptionDetails?.annualTermEndDate ??
          userSubscriptionDetails?.endDate ??
          (typeof userMembershipNextRenewalDate === "string"
            ? userMembershipNextRenewalDate
            : null) ??
          null
      );

      return (
        explicitEndDate ??
        calculateAnnualTermEndDateFromStartDate(designerMembershipStartDate)
      );
    },
    [
      designerMembershipStartDate,
      membershipStatus?.nextRenewalDate,
      membershipStatus?.subscriptionEndDate,
      userMembershipNextRenewalDate,
      userSubscriptionDetails?.annualTermEndDate,
      userSubscriptionDetails?.endDate,
    ]
  );

  const renewalDateLabel = useMemo(() => {
    const renewalDate =
      parseDateValue(
        membershipStatus?.nextRenewalDate ??
          (typeof userMembershipNextRenewalDate === "string"
            ? userMembershipNextRenewalDate
            : null)
      ) ??
      (() => {
        const fallbackDate = new Date();
        fallbackDate.setMonth(fallbackDate.getMonth() + 10);
        return fallbackDate;
      })();

    return new Intl.DateTimeFormat("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    }).format(renewalDate);
  }, [membershipStatus?.nextRenewalDate, userMembershipNextRenewalDate]);

  const cancellationEndDate = useMemo(() => {
    return (
      parseDateValue(
        membershipStatus?.subscriptionEndDate ??
          membershipStatus?.nextRenewalDate ??
          userSubscriptionDetails?.annualTermEndDate ??
          userSubscriptionDetails?.endDate ??
          (typeof userMembershipNextRenewalDate === "string"
            ? userMembershipNextRenewalDate
            : null) ??
          null
      ) ?? calculateAnnualTermEndDateFromStartDate(designerMembershipStartDate)
    );
  }, [
    designerMembershipStartDate,
    membershipStatus?.nextRenewalDate,
    membershipStatus?.subscriptionEndDate,
    userMembershipNextRenewalDate,
    userSubscriptionDetails?.annualTermEndDate,
    userSubscriptionDetails?.endDate,
  ]);

  const cancellationEndDateLabel = useMemo(() => {
    if (!cancellationEndDate) {
      return null;
    }
    return new Intl.DateTimeFormat("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    }).format(cancellationEndDate);
  }, [cancellationEndDate]);

  const nextMonthlyPlatformFeeDate = useMemo(() => {
    const rawNextBillingDate =
      membershipStatus?.monthlyPlatformFeeNextBillingDate ??
      (typeof userSubscriptionDetails?.monthlyPlatformFeeNextBillingDate === "string"
        ? userSubscriptionDetails.monthlyPlatformFeeNextBillingDate
        : null);

    const parsedFromStatus = parseDateValue(rawNextBillingDate);
    if (parsedFromStatus) {
      return parsedFromStatus;
    }

    if (membershipStatus?.subscriptionStartDate) {
      const parsedStartDate = parseDateValue(membershipStatus.subscriptionStartDate);
      if (parsedStartDate) {
        const nextBillingDate = new Date(parsedStartDate);
        nextBillingDate.setMonth(nextBillingDate.getMonth() + 1);
        return nextBillingDate;
      }
    }

    return null;
  }, [
    membershipStatus?.monthlyPlatformFeeNextBillingDate,
    membershipStatus?.subscriptionStartDate,
    userSubscriptionDetails?.monthlyPlatformFeeNextBillingDate,
  ]);

  const nextMonthlyPlatformFeeDateLabel = useMemo(() => {
    if (!nextMonthlyPlatformFeeDate) {
      return null;
    }

    return new Intl.DateTimeFormat("en-US", {
      month: "long",
      day: "numeric",
      year: "numeric",
    }).format(nextMonthlyPlatformFeeDate);
  }, [nextMonthlyPlatformFeeDate]);
  const membershipStatusPaymentStatus = String(
    membershipStatus?.paymentStatus ?? ""
  )
    .trim()
    .toLowerCase();
  const hasPersistedCancellationStatus =
    membershipStatusPaymentStatus === "cancelled" ||
    persistedUserPaymentStatus === "cancelled";
  const hasDisabledAutoRenewState =
    autoRenew === false ||
    membershipStatus?.autoRenew === false ||
    persistedUserMembershipAutoRenew === false;
  const hasDesignerSmartGuildCancelledState =
    hasDisabledAutoRenewState || hasPersistedCancellationStatus;

  const hasCancelledDesignerMembershipWindow =
    !isHomeownerAccount &&
    isDesignerPlanTier(currentPlanTier) &&
    currentPlanTier !== "Home" &&
    hasDesignerSmartGuildCancelledState &&
    Boolean(cancellationEndDate && cancellationEndDate.getTime() > Date.now());

  const hasCancellationScheduled =
    (hasDesignerSmartGuildCancelledState || hasCancelledDesignerMembershipWindow) &&
    Boolean(cancellationEndDate && cancellationEndDate.getTime() > Date.now()) &&
    (isHomeownerAccount ? currentPlanTier !== "Basic" : true);

  const hasActiveDesignerMembership =
    !isHomeownerAccount &&
    isDesignerPlanTier(currentPlanTier) &&
    currentPlanTier !== "Home" &&
    (!designerMembershipEndDate || designerMembershipEndDate.getTime() > Date.now()) &&
    !hasCancelledDesignerMembershipWindow;

  const hasExpiredDesignerMembership =
    !isHomeownerAccount &&
    isDesignerPlanTier(currentPlanTier) &&
    currentPlanTier !== "Home" &&
    Boolean(designerMembershipEndDate && designerMembershipEndDate.getTime() <= Date.now());

  const designerSmartGuildRemainingMonths = useMemo(
    () =>
      countRemainingMonthlyCyclesInAnnualTerm({
        termStartDate: designerMembershipStartDate,
        termEndDate: designerMembershipEndDate,
      }),
    [designerMembershipEndDate, designerMembershipStartDate]
  );

  const designerSmartGuildCanReactivateCurrentPlan =
    !isHomeownerAccount &&
    currentPlanTier === "Design" &&
    designerSmartGuildRemainingMonths > 0 &&
    hasCancelledDesignerMembershipWindow;

  const designerSmartGuildReactivationDueNowAmount =
    designerSmartGuildCanReactivateCurrentPlan &&
    nextMonthlyPlatformFeeDate &&
    nextMonthlyPlatformFeeDate.getTime() > Date.now()
      ? 0
      : DESIGNER_SMART_GUILD_MONTHLY_FEE_DEFAULT;

  const showAutoRenewControls = isHomeownerAccount && !hasCancellationScheduled;
  const showDesignerCadenceToggle = false;
  const isDesignerApplicationCompleted =
    !isDesignerAccount ||
    hasActiveDesignerMembership ||
    (application?.status != null &&
      application.status !== DesignerApplicationSteps.ToBeCompleted &&
      application.status !== DesignerApplicationSteps.CompleteApplication);

  const subscriptionScheduleMessage =
    !isHomeownerAccount && !hasActiveDesignerMembership
      ? "No membership is active yet."
      : hasCancellationScheduled
        ? `Current subscription will end on ${cancellationEndDateLabel}.`
        : autoRenew
          ? `Your subscription will auto renew on ${renewalDateLabel}.`
          : cancellationEndDateLabel
            ? `Auto-renew is off. Current subscription ends on ${cancellationEndDateLabel}.`
            : "Auto-renew is off.";

  const designerCancellationPolicyMessage = useMemo(() => {
    const annualMarketingFeeLabel = new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(DESIGNER_SMART_GUILD_ANNUAL_FEE_DEFAULT);

    return `This is an annual (12-month) membership. You may cancel the membership at any time to cancel the $${DESIGNER_SMART_GUILD_MONTHLY_FEE_DEFAULT} monthly platform fee. The ${annualMarketingFeeLabel} marketing fee is non-refundable. After cancellation, you will have access to the platform until the end of the current month.`;
  }, []);

  const cancellationModalMessage = useMemo(() => {
    if (!isHomeownerAccount) {
      return designerCancellationPolicyMessage;
    }

    if (currentPlanTier === "Guild") {
      return "You will have access to your Hub through the end of the current month.";
    }

    if (currentPlanTier === "Guild+") {
      return "You will have access to your Hub through the end of your 12 month subscription.";
    }

    return "If you cancel this subscription, it will still be available until the end of your current billing period.";
  }, [currentPlanTier, designerCancellationPolicyMessage, isHomeownerAccount]);

  const rawSubscription = useMemo(() => {
    if (typeof membershipStatus?.subscription === "string" && membershipStatus.subscription.trim()) {
      return membershipStatus.subscription;
    }

    if (typeof user?.subscription === "string") {
      return user.subscription;
    }

    const wrapped = user?.subscription as
      | { homeownerValue?: string; designerValue?: string; value?: string; tier?: string }
      | undefined;

    return (
      wrapped?.designerValue ??
      wrapped?.homeownerValue ??
      wrapped?.value ??
      wrapped?.tier ??
      userSubscriptionDetails?.planType ??
      ""
    );
  }, [membershipStatus?.subscription, user?.subscription, userSubscriptionDetails?.planType]);

  useEffect(() => {
    syncMembershipStatusRef.current = syncMembershipStatus;
  }, [syncMembershipStatus]);

  useEffect(() => {
    setProfileForm(mapUserToProfileForm((user as Record<string, unknown> | null) ?? null));
  }, [user]);

  useEffect(() => {
    if (isHomeownerAccount) {
      const inferred = inferHomeownerTierAndCadence(rawSubscription);
      const membershipTier =
        normalizeHomeownerTier(membershipStatus?.subscription) ??
        normalizeHomeownerTier(rawSubscription) ??
        normalizeHomeownerTier(membershipStatus?.currentPlanTier) ??
        normalizeHomeownerTier(userSubscriptionDetails?.planType) ??
        inferred.tier;
      const membershipCadence = normalizeMembershipCadence(
        membershipStatus?.billingCycle ??
          userSubscriptionDetails?.billingCycle ??
          rawSubscription,
        inferred.cadence ?? "monthly"
      );

      setCurrentPlanTier(membershipTier ?? "Basic");
      setCurrentPlanCadence(membershipCadence);
      setSelectedCadence(membershipCadence);
    } else {
      const inferred = inferPlanTierAndCadence(rawSubscription);
      const membershipTier =
        normalizeDesignerTier(membershipStatus?.subscription) ??
        normalizeDesignerTier(rawSubscription) ??
        normalizeDesignerTier(membershipStatus?.currentPlanTier) ??
        normalizeDesignerTier(userSubscriptionDetails?.planType) ??
        inferred.tier;
      setCurrentPlanTier(membershipTier ?? "Home");
      // Designer marketing membership is presented as a single annual card.
      setCurrentPlanCadence("annually");
      setSelectedCadence("annually");
    }

    if (typeof membershipStatus?.autoRenew === "boolean") {
      setAutoRenew(membershipStatus.autoRenew);
    } else if (typeof persistedUserMembershipAutoRenew === "boolean") {
      setAutoRenew(persistedUserMembershipAutoRenew);
    } else if (persistedUserPaymentStatus === "cancelled") {
      setAutoRenew(false);
    }
  }, [
    isHomeownerAccount,
    membershipStatus?.autoRenew,
    membershipStatus?.billingCycle,
    membershipStatus?.currentPlanTier,
    membershipStatus?.subscription,
    persistedUserMembershipAutoRenew,
    persistedUserPaymentStatus,
    rawSubscription,
    userSubscriptionDetails?.billingCycle,
    userSubscriptionDetails?.planType,
  ]);

  useEffect(() => {
    if (!isDesignerApplicationCompleted && activeTab === "subscription") {
      setActiveTab("profile");
    }
  }, [activeTab, isDesignerApplicationCompleted]);

  useEffect(() => {
    let cancelled = false;

    const loadDesignerPlans = async () => {
      setIsPlansLoading(true);
      try {
        const planList = await getPlanList();
        if (cancelled) {
          return;
        }

        const planItems = Array.isArray(planList?.items) ? planList.items : [];
        const designerPlanItems = planItems.filter((plan) => Boolean(inferTierFromPlan(plan)));
        const homeownerPlanItems = planItems.filter((plan) =>
          Boolean(inferHomeownerTierFromPlan(plan))
        );

        setDesignerPlans(mergeDesignerPlansFromHighLevel(designerPlanItems));
        setHomeownerPlans(mergeHomeownerPlansFromHighLevel(homeownerPlanItems));
      } catch (error) {
        if (!cancelled) {
          setDesignerPlans(createEmptyDesignerPlans());
          setHomeownerPlans(createEmptyHomeownerPlans());
        }
      } finally {
        if (!cancelled) {
          setIsPlansLoading(false);
        }
      }
    };

    loadDesignerPlans();

    return () => {
      cancelled = true;
    };
  }, [getPlanList]);

  useEffect(() => {
    if (!hostedToken || !hostedFormRef.current) {
      return;
    }

    hostedFormRef.current.submit();
  }, [hostedToken]);

  const passwordRules = useMemo(
    () => ({
      length: passwordForm.newPassword.length >= 8 && passwordForm.newPassword.length <= 30,
      number: /\d/.test(passwordForm.newPassword),
      letter: /[A-Za-z]/.test(passwordForm.newPassword),
      special: /[^A-Za-z0-9]/.test(passwordForm.newPassword),
    }),
    [passwordForm.newPassword]
  );

  const canSavePassword =
    passwordForm.currentPassword.length > 0 && Object.values(passwordRules).every(Boolean);

  const hostedFormAction = AUTHORIZE_NET_CUSTOMER_PROFILE_ENDPOINT;

  const loadPaymentMethods = useCallback(async () => {
    if (!user?.id) {
      setPaymentMethods([]);
      setPaymentMethodsError(null);
      return;
    }

    setIsPaymentMethodsLoading(true);

    try {
      const response = await request<
        GetAuthorizeNetPaymentMethodsResponse,
        GetAuthorizeNetPaymentMethodsVariables
      >(GRAPHQL_ENDPOINT, GET_AUTHORIZE_NET_PAYMENT_METHODS_QUERY, {
        userId: user.id,
      });

      setPaymentMethods(response?.getAuthorizeNetPaymentMethods ?? []);
      setPaymentMethodsError(null);
    } catch (error) {
      setPaymentMethods([]);
      const message =
        error instanceof Error ? error.message : "Unable to load payment methods.";
      setPaymentMethodsError(message);
    } finally {
      setIsPaymentMethodsLoading(false);
    }
  }, [user?.id]);

  const loadMembershipStatus = useCallback(async (options?: { applyResult?: boolean }) => {
    const shouldApplyResult = options?.applyResult ?? true;

    if (!user?.id) {
      if (shouldApplyResult) {
        setMembershipStatus(null);
      }
      return null;
    }

    setIsMembershipStatusLoading(true);

    try {
      const response = await request<
        GetAccountMembershipStatusResponse,
        GetAccountMembershipStatusVariables
      >(GRAPHQL_ENDPOINT, GET_ACCOUNT_MEMBERSHIP_STATUS_QUERY, {
        userId: user.id,
      });

      const nextStatus = response?.getAccountMembershipStatus ?? null;
      if (shouldApplyResult) {
        setMembershipStatus(nextStatus);
        if (nextStatus) {
          syncMembershipStatusRef.current(nextStatus);
        }
      }
      return nextStatus;
    } catch (error) {
      return null;
    } finally {
      setIsMembershipStatusLoading(false);
    }
  }, [user?.id]);

  const applyMembershipStatusSnapshot = useCallback(
    (snapshot?: AccountMembershipStatus | null) => {
      if (!snapshot) {
        return;
      }

      setMembershipStatus(snapshot);
      syncMembershipStatusRef.current(snapshot);
    },
    []
  );

  const reconcileMembershipStatusAfterMutation = useCallback(
    async (
      mutationSnapshot?: AccountMembershipStatus | null,
      expectedAutoRenew?: boolean
    ) => {
      const refreshedMembershipStatus = await loadMembershipStatus({
        applyResult: false,
      });
      const resolvedMembershipStatus = resolveMembershipStatusAfterMutation({
        mutationSnapshot,
        refreshedSnapshot: refreshedMembershipStatus,
        expectedAutoRenew,
      });

      if (resolvedMembershipStatus) {
        applyMembershipStatusSnapshot(resolvedMembershipStatus);
      }

      return resolvedMembershipStatus;
    },
    [applyMembershipStatusSnapshot, loadMembershipStatus]
  );

  const persistMembershipUpdate = useCallback(
    async (variables: Omit<UpdateAccountMembershipVariables, "userId">) => {
      if (!user?.id) {
        return null;
      }

      setIsMembershipUpdateLoading(true);

      try {
        const response = await request<
          UpdateAccountMembershipResponse,
          UpdateAccountMembershipVariables
        >(GRAPHQL_ENDPOINT, UPDATE_ACCOUNT_MEMBERSHIP_MUTATION, {
          userId: user.id,
          subscription: variables.subscription ?? undefined,
          billingCycle: variables.billingCycle ?? undefined,
          autoRenew: variables.autoRenew ?? undefined,
          cancelAtPeriodEnd: variables.cancelAtPeriodEnd ?? undefined,
        });

        const nextStatus = response?.updateAccountMembership ?? null;
        applyMembershipStatusSnapshot(nextStatus);
        return nextStatus;
      } catch (error) {
        return null;
      } finally {
        setIsMembershipUpdateLoading(false);
      }
    },
    [applyMembershipStatusSnapshot, user?.id]
  );

  const callRecurringSubscriptionApi = useCallback(
    async (
      operation: RecurringSubscriptionOperation,
      payload: Record<string, unknown>
    ): Promise<RecurringSubscriptionResponse> => {
      const endpoint = buildApiUrl(`/api/subscriptions/${operation}`);
      const response = await fetch(endpoint, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });

      let parsed: RecurringSubscriptionResponse = {};
      try {
        parsed = (await response.json()) as RecurringSubscriptionResponse;
      } catch (error) {
        parsed = {};
      }

      if (!response.ok || parsed.success === false) {
        const message =
          parsed.error ||
          parsed.message ||
          `Unable to ${operation} subscription right now.`;
        throw new Error(message);
      }

      return parsed;
    },
    []
  );

  useEffect(() => {
    void loadMembershipStatus();
  }, [loadMembershipStatus]);

  const visiblePlanTierOrder = useMemo<PlanTier[]>(
    () => (isHomeownerAccount ? HOMEOWNER_PLAN_TIER_ORDER : DESIGNER_PLAN_TIER_ORDER),
    [isHomeownerAccount]
  );

  const getPlanForTierAndCadence = useCallback(
    (tier: PlanTier, cadence: BillingCadence): Plan | undefined => {
      if (isHomeownerPlanTier(tier)) {
        return homeownerPlans[tier].plansByCadence[cadence];
      }

      if (isDesignerPlanTier(tier)) {
        return designerPlans[tier].plansByCadence[cadence];
      }

      return undefined;
    },
    [designerPlans, homeownerPlans]
  );

  const getDisplayedCadenceForTier = useCallback(
    (tier: PlanTier): BillingCadence => {
      if (!isHomeownerAccount) {
        return "annually";
      }

      if (!isHomeownerPlanTier(tier)) {
        return selectedCadence;
      }

      const preferredCadences = getPreferredHomeownerCadences(tier);
      const matchedCadence = preferredCadences.find(
        (cadence) => Boolean(homeownerPlans[tier].plansByCadence[cadence])
      );

      return matchedCadence ?? selectedCadence;
    },
    [homeownerPlans, isHomeownerAccount, selectedCadence]
  );

  const getSubscriptionValueForTierAndCadence = useCallback(
    (tier: PlanTier, cadence: BillingCadence): string | null => {
      if (isHomeownerAccount && isHomeownerPlanTier(tier)) {
        return getHomeownerSubscriptionValue(tier, cadence);
      }

      if (!isHomeownerAccount && isDesignerPlanTier(tier)) {
        const mappedPlan = getPlanForTierAndCadence(tier, cadence);
        const metadataSubscriptionValue = asString(
          mappedPlan?.metadata?.subscriptionValue
        );
        if (metadataSubscriptionValue) {
          return metadataSubscriptionValue;
        }
        return getDesignerSubscriptionValue(tier, cadence);
      }

      return null;
    },
    [getPlanForTierAndCadence, isHomeownerAccount]
  );

  const activePlanPrice = pendingPlanTier
    ? getPlanPriceValue(
        getPlanForTierAndCadence(
          pendingPlanTier,
          pendingPlanCadence ?? getDisplayedCadenceForTier(pendingPlanTier)
        )
      )
    : 0;
  const pendingPlan = pendingPlanTier
    ? getPlanForTierAndCadence(
        pendingPlanTier,
        pendingPlanCadence ?? getDisplayedCadenceForTier(pendingPlanTier)
      )
    : undefined;
  const pendingDesignerSmartGuildPresentation = pendingPlanTier
    ? resolveDesignerSmartGuildPresentation({
        isHomeownerAccount,
        tier: isDesignerPlanTier(pendingPlanTier) ? pendingPlanTier : null,
        plan: pendingPlan,
      })
    : {
        shouldUseSmartGuildPresentation: false,
        billingSplit: null,
        features: null,
      };
  const pendingDesignerSmartGuildSplit =
    pendingDesignerSmartGuildPresentation.billingSplit;
  const isPendingDesignerSmartGuildReactivation =
    Boolean(
      pendingDesignerSmartGuildSplit &&
        pendingPlanTier &&
        pendingPlanTier === currentPlanTier &&
        designerSmartGuildCanReactivateCurrentPlan
    );
  const pendingPlanDueNowPrice =
    isPendingDesignerSmartGuildReactivation
      ? designerSmartGuildReactivationDueNowAmount
      : pendingDesignerSmartGuildSplit?.dueNowAmount ?? activePlanPrice;
  const upgradeDialogTitle = isPendingDesignerSmartGuildReactivation
    ? "Reactivate Subscription"
    : "Purchase Subscription";
  const upgradeDialogPrimaryActionLabel = isPendingDesignerSmartGuildReactivation
    ? "REACTIVE"
    : "PURCHASE";

  const getPlanActionType = useCallback(
    (tier: PlanTier, cadence: BillingCadence): "current" | "upgrade" | "downgrade" => {
      if (isHomeownerAccount && isHomeownerPlanTier(tier) && isHomeownerPlanTier(currentPlanTier)) {
        if (tier === currentPlanTier) {
          return hasCancellationScheduled ? "upgrade" : "current";
        }
      }

      if (tier === currentPlanTier && cadence === currentPlanCadence) {
        if (!isHomeownerAccount) {
          const designerCurrentPlanButtonState =
            resolveDesignerCurrentPlanButtonState({
              hasCancelledState: hasDesignerSmartGuildCancelledState,
              hasRemainingPrepaidTerm: designerSmartGuildCanReactivateCurrentPlan,
              hasExpiredMembership: hasExpiredDesignerMembership,
            });

          return designerCurrentPlanButtonState === "current"
            ? "current"
            : "upgrade";
        }

        return hasCancellationScheduled ? "upgrade" : "current";
      }

      const currentRank = isHomeownerAccount
        ? isHomeownerPlanTier(currentPlanTier)
          ? HOMEOWNER_PLAN_TIER_RANK[currentPlanTier]
          : null
        : isDesignerPlanTier(currentPlanTier)
          ? DESIGNER_PLAN_TIER_RANK[currentPlanTier]
          : null;
      const targetRank = isHomeownerAccount
        ? isHomeownerPlanTier(tier)
          ? HOMEOWNER_PLAN_TIER_RANK[tier]
          : null
        : isDesignerPlanTier(tier)
          ? DESIGNER_PLAN_TIER_RANK[tier]
          : null;

      if (typeof currentRank === "number" && typeof targetRank === "number") {
        if (targetRank > currentRank) {
          return "upgrade";
        }
        if (targetRank < currentRank) {
          return "downgrade";
        }
      }

      const currentPlan = getPlanForTierAndCadence(currentPlanTier, currentPlanCadence);
      const targetPlan = getPlanForTierAndCadence(tier, cadence);
      const currentPrice = getPlanPriceValue(currentPlan);
      const targetPrice = getPlanPriceValue(targetPlan);

      return targetPrice >= currentPrice ? "upgrade" : "downgrade";
    },
    [
      currentPlanCadence,
      currentPlanTier,
      hasDesignerSmartGuildCancelledState,
      designerSmartGuildCanReactivateCurrentPlan,
      getPlanForTierAndCadence,
      hasExpiredDesignerMembership,
      hasCancellationScheduled,
      isHomeownerAccount,
    ]
  );

  const hostedPrimaryActionLabel = "I Updated Payment Methods";

  useEffect(() => {
    if (activeTab !== "billing" || billingMode !== "list") {
      return;
    }

    void loadPaymentMethods();
  }, [activeTab, billingMode, loadPaymentMethods]);

  useEffect(() => {
    const onHostedMessage = (event: MessageEvent) => {
      if (billingMode !== "hosted") {
        return;
      }

      if (!event.data || typeof event.data !== "object") {
        return;
      }

      const payload = event.data as {
        type?: string;
        action?: string;
        height?: string | number;
      };

      if (payload.type !== "AUTHORIZE_NET_IFRAME") {
        return;
      }

      const action = String(payload.action ?? "").toLowerCase();

      if (action === "resizewindow") {
        const frame = document.getElementById("my-account-authorize-hosted-frame") as
          | HTMLIFrameElement
          | null;
        const nextHeight = Number(payload.height);

        if (frame && Number.isFinite(nextHeight) && nextHeight > 0) {
          frame.style.height = `${Math.max(nextHeight, 420)}px`;
        }

        return;
      }

      if (action === "successfulsave" || action === "cancel") {
        setBillingMode("list");
        setBillingRequest(null);
        setHostedToken("");
        setHostedError(null);
        setIsHostedLoading(false);
        void loadPaymentMethods();
      }
    };

    window.addEventListener("message", onHostedMessage);

    return () => {
      window.removeEventListener("message", onHostedMessage);
    };
  }, [billingMode, loadPaymentMethods]);

  const openHostedCheckout = useCallback(
    async (requestConfig: HostedCheckoutRequest) => {
      if (!user?.email || !user?.id) {
        showError("Missing account email. Please refresh and try again.");
        return;
      }

      const customerInfo = {
        firstName: profileForm.firstName.trim() || String(user.firstName ?? "Account"),
        lastName: profileForm.lastName.trim() || String(user.lastName ?? "Holder"),
        address: profileForm.mailingAddress.trim() || "Address pending",
        city: profileForm.city.trim() || "Collierville",
        state: (profileForm.businessState.trim() || "TN").toUpperCase().slice(0, 2),
        zipCode: profileForm.zipCode.trim() || "38017",
      };

      setActiveTab("billing");
      setBillingMode("hosted");
      setBillingRequest(requestConfig);
      setHostedToken("");
      setHostedError(null);
      setHostedFrameKey((prev) => prev + 1);
      setIsHostedLoading(true);

      try {
        const payload: GetAcceptCustomerProfileTokenVariables = {
          userId: user.id,
          email: user.email,
          customerInfo,
        };

        const response = await request<
          GetAcceptCustomerProfileTokenResponse,
          GetAcceptCustomerProfileTokenVariables
        >(GRAPHQL_ENDPOINT, GET_ACCEPT_CUSTOMER_PROFILE_TOKEN_MUTATION, payload);

        if (!response?.getAcceptCustomerProfileToken) {
          throw new Error("Unable to initialize secure payment method form.");
        }

        setHostedToken(response.getAcceptCustomerProfileToken);
      } catch (error) {
        const message =
          error instanceof Error ? error.message : "Failed to load secure billing form.";
        setHostedError(message);
      } finally {
        setIsHostedLoading(false);
      }
    },
    [
      profileForm.businessState,
      profileForm.city,
      profileForm.firstName,
      profileForm.lastName,
      profileForm.mailingAddress,
      profileForm.zipCode,
      showError,
      user,
    ]
  );

  const handleTabChange = (_: SyntheticEvent, value: AccountTabValue) => {
    if (value === "subscription" && !isDesignerApplicationCompleted) {
      return;
    }
    setActiveTab(value);
  };

  const handleProfileFieldChange =
    (field: keyof ProfileFormState) =>
    (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      const nextValue = event.target.value;
      setProfileForm((current) => ({ ...current, [field]: nextValue }));
    };

  const handlePasswordFieldChange =
    (field: keyof PasswordFormState) =>
    (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      const nextValue = event.target.value;
      setPasswordForm((current) => ({ ...current, [field]: nextValue }));
    };

  const handleSaveProfile = async () => {
    if (!user?.id) {
      return;
    }

    setIsSavingProfile(true);

    try {
      if (isDesignerAccount) {
        await updateDesignerAccountData({
          _id: user.id,
          firstName: profileForm.firstName || undefined,
          lastName: profileForm.lastName || undefined,
          email: profileForm.email || undefined,
          phoneNumber: profileForm.phoneNumber || undefined,
          businessName: profileForm.businessName || undefined,
          businessAddress: profileForm.mailingAddress || undefined,
          businessCity: profileForm.city || undefined,
          businessState: (profileForm.businessState || undefined) as States | undefined,
          businessZipCode: profileForm.zipCode || undefined,
          businessWebsite: profileForm.businessWebsite || undefined,
        });
      } else if (isHomeownerAccount) {
        await updateHomeownerAccountData({
          _id: user.id,
          firstName: profileForm.firstName || undefined,
          lastName: profileForm.lastName || undefined,
          email: profileForm.email || undefined,
          phoneNumber: profileForm.phoneNumber || undefined,
          address: profileForm.mailingAddress || undefined,
          city: profileForm.city || undefined,
          state: profileForm.businessState || undefined,
          zipCode: profileForm.zipCode || undefined,
        });
      } else {
        showError("Account editing is not available for this role.");
        return;
      }

      showSuccess("Profile changes saved.");
    } catch (error) {
      showError("Unable to save profile changes.");
    } finally {
      setIsSavingProfile(false);
    }
  };

  const handleSavePassword = async () => {
    if (!user?.email || !canSavePassword) {
      return;
    }

    setIsSavingPassword(true);

    try {
      const result = await resetUserPassword(user.email, passwordForm.newPassword);
      if (result !== true) {
        throw new Error("Unable to update password");
      }

      setPasswordForm({ currentPassword: "", newPassword: "" });
      showSuccess("Password updated.");
    } catch (error) {
      showError("Unable to update password.");
    } finally {
      setIsSavingPassword(false);
    }
  };

  const handleRemovePaymentMethod = () => {
    openHostedCheckout({
      mode: "edit",
      heading: "Manage Payment Methods",
      completionMessage: "Payment methods updated.",
    });
  };

  const handleOpenAddPayment = () => {
    openHostedCheckout({
      mode: "add",
      heading: "Add New Payment Method",
      completionMessage: "Payment method flow completed.",
    });
  };

  const handleOpenEditPayment = () => {
    openHostedCheckout({
      mode: "edit",
      heading: "Update Payment Method",
      completionMessage: "Payment method update flow completed.",
    });
  };

  const handleHostedBack = () => {
    setBillingMode("list");
    setBillingRequest(null);
    setHostedToken("");
    setHostedError(null);
    setIsHostedLoading(false);
    void loadPaymentMethods();
  };

  const handleHostedCompletion = async () => {
    showSuccess(
      billingRequest?.completionMessage || "Billing action marked as completed."
    );

    setBillingMode("list");
    setBillingRequest(null);
    setHostedToken("");
    setHostedError(null);

    await loadPaymentMethods();
  };

  const applyRecurringPlanChange = useCallback(
    async (tier: PlanTier, cadence: BillingCadence) => {
      if (!user?.id) {
        throw new Error("Missing account ID. Please refresh and try again.");
      }

      const targetPlan = getPlanForTierAndCadence(tier, cadence);
      const targetPrice = getPlanPriceValue(targetPlan);
      const smartGuildBillingSplit = resolveDesignerSmartGuildPresentation({
        isHomeownerAccount,
        tier: isDesignerPlanTier(tier) ? tier : null,
        plan: targetPlan,
      }).billingSplit;
      const isDesignerSmartGuildReactivation =
        Boolean(
          smartGuildBillingSplit &&
            !isHomeownerAccount &&
            tier === currentPlanTier &&
            designerSmartGuildCanReactivateCurrentPlan
        );
      const upgradeAmount =
        isDesignerSmartGuildReactivation
          ? designerSmartGuildReactivationDueNowAmount
          : smartGuildBillingSplit?.dueNowAmount ?? targetPrice;
      const requestedAutoRenewForUpgrade = smartGuildBillingSplit ? true : autoRenew;
      const subscriptionValue = getSubscriptionValueForTierAndCadence(tier, cadence);

      if (!subscriptionValue) {
        throw new Error("Unable to resolve selected plan.");
      }

      const freeTargetPlan = isFreeSubscriptionValue(subscriptionValue);

      if (!freeTargetPlan && upgradeAmount < 0) {
        throw new Error("Selected plan pricing is unavailable. Please refresh and try again.");
      }

      let mutationSnapshot: AccountMembershipStatus | null = null;

      if (freeTargetPlan) {
        try {
          const cancelResponse = await callRecurringSubscriptionApi("cancel", {
            userId: user.id,
          });
          mutationSnapshot = cancelResponse.membership ?? null;
          applyMembershipStatusSnapshot(mutationSnapshot);
        } catch (error) {
          const message = error instanceof Error ? error.message : "";
          if (!isMissingRecurringSubscriptionError(message)) {
            throw error;
          }
        }

        const persistedMembershipStatus = await persistMembershipUpdate({
          subscription: subscriptionValue,
          billingCycle: cadence,
          autoRenew: false,
        });
        mutationSnapshot = persistedMembershipStatus ?? mutationSnapshot;
      } else {
        const upgradeResponse = await callRecurringSubscriptionApi("upgrade", {
          userId: user.id,
          planName: subscriptionValue,
          amount: Number(upgradeAmount.toFixed(2)),
          billingCycle: cadence,
          autoRenew: requestedAutoRenewForUpgrade,
          fallbackToCancelAndCreate: true,
          ...(smartGuildBillingSplit
            ? {
                annualFeeAmount: isDesignerSmartGuildReactivation
                  ? 0
                  : smartGuildBillingSplit.annualFeeAmount,
                monthlyFeeAmount: isDesignerSmartGuildReactivation
                  ? DESIGNER_SMART_GUILD_MONTHLY_FEE_DEFAULT
                  : smartGuildBillingSplit.monthlyFeeAmount,
                dueNowAmount: upgradeAmount,
              }
            : {}),
        });
        mutationSnapshot = upgradeResponse.membership ?? null;
        applyMembershipStatusSnapshot(mutationSnapshot);
      }

      const resolvedMembershipStatus = await reconcileMembershipStatusAfterMutation(
        mutationSnapshot,
        freeTargetPlan ? false : requestedAutoRenewForUpgrade
      );
      if (!resolvedMembershipStatus && !mutationSnapshot) {
        return;
      }
    },
    [
      applyMembershipStatusSnapshot,
      callRecurringSubscriptionApi,
      getPlanForTierAndCadence,
      getSubscriptionValueForTierAndCadence,
      persistMembershipUpdate,
      reconcileMembershipStatusAfterMutation,
      user?.id,
      autoRenew,
      currentPlanTier,
      designerSmartGuildCanReactivateCurrentPlan,
      designerSmartGuildReactivationDueNowAmount,
      isHomeownerAccount,
    ]
  );

  const shouldRedirectToBillingForError = (message: string): boolean => {
    const normalized = message.toLowerCase();
    return (
      normalized.includes("add a payment method first") ||
      normalized.includes("no payment profile found") ||
      normalized.includes("no authorize.net customer profile found")
    );
  };

  const openAddPaymentMethodPrompt = useCallback(() => {
    setUpgradeDialogOpen(false);
    setDowngradeDialogOpen(false);
    setAddPaymentMethodPromptOpen(true);
  }, []);

  const handleGoToAddPaymentMethod = useCallback(() => {
    setAddPaymentMethodPromptOpen(false);
    setActiveTab("billing");
    setBillingMode("list");
  }, []);

  const isMissingRecurringSubscriptionError = (message: string): boolean =>
    message.toLowerCase().includes("no active authorize.net subscriptionid found");

  const handlePlanCardAction = (tier: PlanTier) => {
    const targetCadence = getDisplayedCadenceForTier(tier);
    const actionType = getPlanActionType(tier, targetCadence);
    if (actionType === "current") {
      return;
    }

    const targetPlan = getPlanForTierAndCadence(tier, targetCadence);
    if (!targetPlan) {
      showError(`No ${tier} plan is configured for ${CADENCE_LABEL[targetCadence]}.`);
      return;
    }

    setPendingPlanTier(tier);
    setPendingPlanCadence(targetCadence);

    if (actionType === "upgrade") {
      setUpgradeDialogOpen(true);
    } else {
      setDowngradeDialogOpen(true);
    }
  };

  const handleUpgradeConfirm = async () => {
    if (!pendingPlanTier) {
      return;
    }

    const targetCadence = pendingPlanCadence ?? getDisplayedCadenceForTier(pendingPlanTier);
    const pendingPlanForAction = getPlanForTierAndCadence(
      pendingPlanTier,
      targetCadence
    );
    const pendingSplit = resolveDesignerSmartGuildPresentation({
      isHomeownerAccount,
      tier: isDesignerPlanTier(pendingPlanTier) ? pendingPlanTier : null,
      plan: pendingPlanForAction,
    }).billingSplit;
    const isDesignerSmartGuildReactivation =
      Boolean(
        pendingSplit &&
          !isHomeownerAccount &&
          pendingPlanTier === currentPlanTier &&
          designerSmartGuildCanReactivateCurrentPlan
      );
    const minimumCharge = isDesignerSmartGuildReactivation
      ? designerSmartGuildReactivationDueNowAmount
      : pendingSplit?.dueNowAmount ?? activePlanPrice;

    if (!pendingPlanForAction || minimumCharge < 0) {
      showError("Selected plan pricing is unavailable. Please refresh and try again.");
      return;
    }

    if (!upgradeConsentChecked) {
      showError("Please accept the charge acknowledgement before continuing.");
      return;
    }

    setIsMembershipUpdateLoading(true);

    try {
      await applyRecurringPlanChange(pendingPlanTier, targetCadence);
      setUpgradeDialogOpen(false);
      setPendingPlanTier(null);
      setPendingPlanCadence(null);
      showSuccess(`Plan changed to ${pendingPlanTier}.`);
    } catch (error) {
      const message =
        error instanceof Error ? error.message : "Unable to update subscription.";
      if (shouldRedirectToBillingForError(message)) {
        openAddPaymentMethodPrompt();
        return;
      }
      showError(message);
    } finally {
      setIsMembershipUpdateLoading(false);
    }
  };

  const handleAutoRenewToggle = async (nextValue: boolean) => {
    const previousValue = autoRenew;
    setAutoRenew(nextValue);

    const currentSubscription = getSubscriptionValueForTierAndCadence(
      currentPlanTier,
      currentPlanCadence
    );
    const effectiveSubscription = currentSubscription || rawSubscription;
    const hasPaidCurrentSubscription =
      typeof effectiveSubscription === "string" &&
      effectiveSubscription.trim().length > 0 &&
      !isFreeSubscriptionValue(effectiveSubscription);

    setIsMembershipUpdateLoading(true);

    try {
      let mutationSnapshot: AccountMembershipStatus | null = null;

      if (!nextValue) {
        if (hasPaidCurrentSubscription) {
          if (!user?.id) {
            throw new Error("Missing account ID. Please refresh and try again.");
          }

          try {
            const cancelResponse = await callRecurringSubscriptionApi("cancel", {
              userId: user.id,
            });
            mutationSnapshot = cancelResponse.membership ?? null;
            applyMembershipStatusSnapshot(mutationSnapshot);
          } catch (error) {
            const message =
              error instanceof Error ? error.message : "Unable to cancel subscription.";
            if (!isMissingRecurringSubscriptionError(message)) {
              throw error;
            }

            mutationSnapshot = await persistMembershipUpdate({
              autoRenew: false,
              cancelAtPeriodEnd: true,
            });

            await reconcileMembershipStatusAfterMutation(mutationSnapshot, false);
            showSuccess("Auto-renew disabled.");
            return;
          }
        } else {
          mutationSnapshot = await persistMembershipUpdate({
            autoRenew: false,
          });
        }

        const resolvedMembershipStatus = await reconcileMembershipStatusAfterMutation(
          mutationSnapshot,
          false
        );
        setAutoRenew(resolvedMembershipStatus?.autoRenew ?? false);
        showSuccess("Auto-renew disabled.");
        return;
      }

      const currentPlan = getPlanForTierAndCadence(currentPlanTier, currentPlanCadence);
      const currentPlanPrice = getPlanPriceValue(currentPlan);

      if (!currentSubscription || currentPlanPrice <= 0) {
        throw new Error("Select a paid plan to enable auto-renew.");
      }

      if (!user?.id) {
        throw new Error("Missing account ID. Please refresh and try again.");
      }

      const upgradeResponse = await callRecurringSubscriptionApi("upgrade", {
        userId: user.id,
        planName: currentSubscription,
        amount: Number(currentPlanPrice.toFixed(2)),
        billingCycle: currentPlanCadence,
        autoRenew: true,
        fallbackToCancelAndCreate: true,
      });
      mutationSnapshot = upgradeResponse.membership ?? null;
      applyMembershipStatusSnapshot(mutationSnapshot);

      const resolvedMembershipStatus = await reconcileMembershipStatusAfterMutation(
        mutationSnapshot,
        true
      );
      setAutoRenew(resolvedMembershipStatus?.autoRenew ?? true);
      showSuccess("Auto-renew enabled.");
    } catch (error) {
      setAutoRenew(previousValue);
      const message =
        error instanceof Error ? error.message : "Unable to update auto-renew preference.";
      if (shouldRedirectToBillingForError(message)) {
        openAddPaymentMethodPrompt();
        return;
      }
      showError(message);
    } finally {
      setIsMembershipUpdateLoading(false);
    }
  };

  const handleDowngradeConfirm = async () => {
    if (!pendingPlanTier) {
      return;
    }

    const targetCadence = pendingPlanCadence ?? getDisplayedCadenceForTier(pendingPlanTier);

    setIsMembershipUpdateLoading(true);

    try {
      await applyRecurringPlanChange(pendingPlanTier, targetCadence);
      setDowngradeDialogOpen(false);
      setPendingPlanTier(null);
      setPendingPlanCadence(null);
      showSuccess(`Plan changed to ${pendingPlanTier}.`);
    } catch (error) {
      const message =
        error instanceof Error ? error.message : "Unable to update plan right now.";
      if (shouldRedirectToBillingForError(message)) {
        openAddPaymentMethodPrompt();
        return;
      }
      showError(message);
    } finally {
      setIsMembershipUpdateLoading(false);
    }
  };

  const handleConfirmCancellation = async () => {
    if (!user?.id) {
      showError("Missing account ID. Please refresh and try again.");
      return;
    }

    const currentSubscription = getSubscriptionValueForTierAndCadence(
      currentPlanTier,
      currentPlanCadence
    );
    const effectiveSubscription = currentSubscription || rawSubscription;
    const hasPaidCurrentSubscription =
      typeof effectiveSubscription === "string" &&
      effectiveSubscription.trim().length > 0 &&
      !isFreeSubscriptionValue(effectiveSubscription);

    setIsMembershipUpdateLoading(true);

    try {
      let mutationSnapshot: AccountMembershipStatus | null = null;

      if (hasPaidCurrentSubscription) {
        try {
          const cancelResponse = await callRecurringSubscriptionApi("cancel", {
            userId: user.id,
          });
          mutationSnapshot = cancelResponse.membership ?? null;
          applyMembershipStatusSnapshot(mutationSnapshot);
        } catch (error) {
          const message =
            error instanceof Error ? error.message : "Unable to cancel subscription.";
          if (!isMissingRecurringSubscriptionError(message)) {
            throw error;
          }

          mutationSnapshot = await persistMembershipUpdate({
            autoRenew: false,
            cancelAtPeriodEnd: true,
          });
        }
      } else {
        mutationSnapshot = await persistMembershipUpdate({
          autoRenew: false,
          cancelAtPeriodEnd: true,
        });
      }

      const resolvedMembershipStatus = await reconcileMembershipStatusAfterMutation(
        mutationSnapshot,
        false
      );
      setCancelDialogOpen(false);
      setFeedbackDialogOpen(true);
      setAutoRenew(resolvedMembershipStatus?.autoRenew ?? false);
    } catch (error) {
      const message =
        error instanceof Error
          ? error.message
          : "Unable to cancel subscription right now. Please try again.";
      showError(message);
    } finally {
      setIsMembershipUpdateLoading(false);
    }
  };

  const handleFeedbackReasonToggle = (reason: string) => {
    setFeedbackReasons((current) =>
      current.includes(reason)
        ? current.filter((item) => item !== reason)
        : [...current, reason]
    );
  };

  const handleFeedbackSubmit = () => {
    setFeedbackDialogOpen(false);
    setFeedbackReasons([]);
    setFeedbackNotes("");
    showSuccess("Feedback submitted. Thank you.");
  };

  const profileTabContent = (
    <Box className="my-account-profile-tab">
      <Typography variant="h5" className="my-account-title-row">
        Welcome, {profileForm.firstName || "Designer"}.
      </Typography>
      <Typography className="my-account-date-label">{todayLabel}</Typography>

      <Box className="my-account-form-columns">
        <Box>
          <Typography className="my-account-form-block-title">Personal Details:</Typography>
          <Box className="my-account-field-grid">
            <TextField
              label="First Name"
              value={profileForm.firstName}
              onChange={handleProfileFieldChange("firstName")}
              size="small"
            />
            <TextField
              label="Last Name"
              value={profileForm.lastName}
              onChange={handleProfileFieldChange("lastName")}
              size="small"
            />
            <TextField
              label="Phone Number"
              value={profileForm.phoneNumber}
              onChange={handleProfileFieldChange("phoneNumber")}
              size="small"
            />
            <TextField
              label="Email"
              value={profileForm.email}
              onChange={handleProfileFieldChange("email")}
              size="small"
            />
          </Box>
        </Box>

        <Box>
          <Typography className="my-account-form-block-title">
            {isHomeownerAccount ? "Home Address:" : "Business Details:"}
          </Typography>
          <Box className="my-account-field-grid my-account-field-grid--business">
            {!isHomeownerAccount && (
              <TextField
                label="Company or Organization"
                value={profileForm.businessName}
                onChange={handleProfileFieldChange("businessName")}
                size="small"
                fullWidth
              />
            )}
            <TextField
              label="Mailing Address"
              value={profileForm.mailingAddress}
              onChange={handleProfileFieldChange("mailingAddress")}
              size="small"
              fullWidth
            />

            <Box className="my-account-location-row">
              <TextField
                label="Zip Code"
                value={profileForm.zipCode}
                onChange={handleProfileFieldChange("zipCode")}
                size="small"
              />
              <TextField
                label="City"
                value={profileForm.city}
                onChange={handleProfileFieldChange("city")}
                size="small"
              />
              <TextField
                select
                label="State"
                value={profileForm.businessState}
                onChange={handleProfileFieldChange("businessState")}
                size="small"
              >
                {stateOptions.map((state) => (
                  <MenuItem key={state} value={state}>
                    {state}
                  </MenuItem>
                ))}
              </TextField>
            </Box>

            {!isHomeownerAccount && (
              <TextField
                label="Business Website"
                value={profileForm.businessWebsite}
                onChange={handleProfileFieldChange("businessWebsite")}
                size="small"
                fullWidth
              />
            )}
          </Box>

          <Box className="my-account-profile-save-row">
            <Button
              variant="contained"
              className="my-account-primary-btn"
              disabled={!canEditAccountDetails || isSavingProfile}
              onClick={handleSaveProfile}
            >
              {isSavingProfile ? "Saving..." : "Save Changes"}
            </Button>
          </Box>
        </Box>
      </Box>

      <Box className="my-account-password-section">
        <Typography className="my-account-form-block-title">Change Your Password:</Typography>
        <Typography className="my-account-muted-copy">
          You can change your password for security reasons.
        </Typography>

        <Box className="my-account-password-grid">
          <TextField
            label="Current Password"
            type="password"
            value={passwordForm.currentPassword}
            onChange={handlePasswordFieldChange("currentPassword")}
            size="small"
          />
          <TextField
            label="New Password"
            type="password"
            value={passwordForm.newPassword}
            onChange={handlePasswordFieldChange("newPassword")}
            size="small"
          />
        </Box>

        <Box className="my-account-password-rules">
          <Typography className="my-account-muted-copy">Your password must have:</Typography>
          <ul>
            <li>
              <CheckCircle fontSize="small" color={passwordRules.length ? "success" : "disabled"} />
              Between 8 and 30 characters
            </li>
            <li>
              <CheckCircle fontSize="small" color={passwordRules.number ? "success" : "disabled"} />
              At least 1 number
            </li>
            <li>
              <CheckCircle fontSize="small" color={passwordRules.letter ? "success" : "disabled"} />
              At least 1 letter
            </li>
            <li>
              <CheckCircle fontSize="small" color={passwordRules.special ? "success" : "disabled"} />
              At least 1 special character
            </li>
          </ul>
        </Box>

        <Box className="my-account-password-save-row">
          <Button
            variant="contained"
            className="my-account-primary-btn"
            disabled={!canSavePassword || isSavingPassword}
            onClick={handleSavePassword}
          >
            {isSavingPassword ? "Saving..." : "Save Changes"}
          </Button>
        </Box>
      </Box>
    </Box>
  );

  const billingTabContent = (
    <Box className="my-account-billing-tab">
      {billingMode === "list" ? (
        <>
          <Typography variant="h5" className="my-account-billing-title">
            Payment Methods
          </Typography>

          {paymentMethodsError && <Alert severity="error">{paymentMethodsError}</Alert>}

          {isPaymentMethodsLoading && (
            <Box className="my-account-hosted-loader">
              <CircularProgress size={30} sx={{ color: "#4f46e5" }} />
              <Typography>Syncing payment methods...</Typography>
            </Box>
          )}

          {!isPaymentMethodsLoading && paymentMethods.length === 0 && (
            <Typography className="my-account-muted-copy">
              No payment methods added yet.
            </Typography>
          )}

          <Box className="my-account-payment-grid">
            {paymentMethods.map((method) => (
              <Paper key={method.id} className="my-account-payment-card" variant="outlined">
                <Box>
                  <Typography className="my-account-card-brand">
                    {method.brand} **** {method.last4}
                  </Typography>
                  <Typography className="my-account-card-expiry">
                    Expires {method.expiresAt}
                  </Typography>
                </Box>
                <Box className="my-account-card-actions">
                  <Button onClick={handleOpenEditPayment}>Edit</Button>
                  <Button color="inherit" onClick={handleRemovePaymentMethod}>
                    Remove
                  </Button>
                </Box>
              </Paper>
            ))}

            <Paper className="my-account-payment-card my-account-payment-card--add" variant="outlined">
              <Button onClick={handleOpenAddPayment}>+ Add payment method</Button>
            </Paper>
          </Box>
        </>
      ) : (
        <>
          <Typography variant="h5" className="my-account-billing-title">
            {billingRequest?.heading || "Secure Billing"}
          </Typography>
          <Typography className="my-account-muted-copy">
            This secure billing step uses Authorize.Net hosted UI.
          </Typography>

          {hostedError && <Alert severity="error">{hostedError}</Alert>}

          {isHostedLoading && (
            <Box className="my-account-hosted-loader">
              <CircularProgress size={36} sx={{ color: "#4f46e5" }} />
              <Typography>Loading secure form...</Typography>
            </Box>
          )}

          <Box className="my-account-hosted-frame-wrapper">
            <iframe
              key={hostedFrameKey}
              id="my-account-authorize-hosted-frame"
              name="my-account-authorize-hosted-frame"
              title="Authorize.Net Hosted Billing Form"
              className="my-account-hosted-frame"
            />

            <form
              ref={hostedFormRef}
              action={hostedFormAction}
              method="post"
              target="my-account-authorize-hosted-frame"
            >
              <input type="hidden" name="token" value={hostedToken} />
            </form>
          </Box>

          <Box className="my-account-hosted-actions">
            <Button startIcon={<KeyboardArrowLeft />} onClick={handleHostedBack}>
              Back
            </Button>
            <Button
              variant="contained"
              className="my-account-primary-btn"
              onClick={handleHostedCompletion}
            >
              {hostedPrimaryActionLabel}
            </Button>
          </Box>
        </>
      )}
    </Box>
  );

  const subscriptionTabContent = (
    <Box className="my-account-subscription-tab">
      <Box className="my-account-subscription-header">
        <Box>
          <Typography variant="h5" className="my-account-subscription-title">
            {isHomeownerAccount ? "Membership Plan" : "Designer Membership"}
          </Typography>
          {showAutoRenewControls && (
            <Box className="my-account-renew-row">
              <Typography>{CADENCE_LABEL[selectedCadence]}</Typography>
              <FormControlLabel
                control={
                  <Switch
                    checked={autoRenew}
                    disabled={isMembershipStatusLoading || isMembershipUpdateLoading}
                    onChange={(event) => {
                      void handleAutoRenewToggle(event.target.checked);
                    }}
                  />
                }
                label="Auto Renew"
                labelPlacement="start"
              />
            </Box>
          )}
          <Typography className="my-account-muted-copy">
            {subscriptionScheduleMessage}
          </Typography>
        </Box>

        {!isHomeownerAccount && showDesignerCadenceToggle && (
          <Stack direction="row" className="my-account-cadence-toggle">
            {(["bi-annually", "annually", "monthly"] as BillingCadence[]).map((cadence) => (
              <Button
                key={cadence}
                size="small"
                onClick={() => setSelectedCadence(cadence)}
                disabled={isMembershipUpdateLoading}
                className={
                  selectedCadence === cadence
                    ? "my-account-cadence-btn is-active"
                    : "my-account-cadence-btn"
                }
              >
                {CADENCE_LABEL[cadence]}
              </Button>
            ))}
          </Stack>
        )}
      </Box>

      {isPlansLoading && (
        <Box className="my-account-hosted-loader">
          <CircularProgress size={30} sx={{ color: "#4f46e5" }} />
          <Typography>Syncing latest membership pricing...</Typography>
        </Box>
      )}

      {isMembershipStatusLoading && (
        <Box className="my-account-hosted-loader">
          <CircularProgress size={26} sx={{ color: "#4f46e5" }} />
          <Typography>Syncing membership status...</Typography>
        </Box>
      )}

      {!isPlansLoading &&
        visiblePlanTierOrder.every(
          (tier) => !getPlanForTierAndCadence(tier, getDisplayedCadenceForTier(tier))
        ) && (
          <Typography className="my-account-muted-copy">
            No membership plans are configured right now.
          </Typography>
        )}

      <Box className="my-account-plans-grid">
        {visiblePlanTierOrder.map((tier) => {
          const cardCadence = getDisplayedCadenceForTier(tier);
          const plan = getPlanForTierAndCadence(tier, cardCadence);
          if (!plan) {
            return null;
          }

          const planPrice = getPlanPriceValue(plan);
          const designerSmartGuildPresentation = resolveDesignerSmartGuildPresentation({
            isHomeownerAccount,
            tier: isDesignerPlanTier(tier) ? tier : null,
            plan,
          });
          const designerSmartGuildSplit =
            designerSmartGuildPresentation.billingSplit;
          const displayedPlanPrice = designerSmartGuildSplit
            ? designerSmartGuildSplit.totalAnnualFeeAmount ??
              DESIGNER_SMART_GUILD_TOTAL_ANNUAL_FEE_DEFAULT
            : planPrice;
          const planOriginalPrice = getPlanOriginalPriceValue(plan);
          const planFeatures =
            designerSmartGuildPresentation.features ??
            getDesignerSmartGuildDisplayedFeatures(plan, plan.benefits);
          const planTitle = (plan.name || tier).replace(/^BETA:\s*/i, "").trim() || tier;
          const isBeta = /beta/i.test(plan.name || "");
          const planDescription = designerSmartGuildSplit
            ? undefined
            : plan.description;
          const actionType = getPlanActionType(tier, cardCadence);
          const isTierCadenceCurrentMatch =
            tier === currentPlanTier && cardCadence === currentPlanCadence;
          const designerCurrentPlanButtonState =
            !isHomeownerAccount && isTierCadenceCurrentMatch
              ? resolveDesignerCurrentPlanButtonState({
                  hasCancelledState: hasDesignerSmartGuildCancelledState,
                  hasRemainingPrepaidTerm: designerSmartGuildCanReactivateCurrentPlan,
                  hasExpiredMembership: hasExpiredDesignerMembership,
                })
              : "current";
          const isDesignerSmartGuildReactivationState =
            Boolean(
              designerSmartGuildSplit &&
                !isHomeownerAccount &&
                isTierCadenceCurrentMatch &&
                designerCurrentPlanButtonState === "reactivate"
            );
          const isDesignerSmartGuildCancelledPurchaseState =
            Boolean(
              designerSmartGuildSplit &&
                !isHomeownerAccount &&
                isTierCadenceCurrentMatch &&
                designerCurrentPlanButtonState === "purchase"
            );
          const isPaidDesignerCurrentPlan =
            !!designerSmartGuildSplit &&
            !isHomeownerAccount &&
            hasActiveDesignerMembership &&
            isTierCadenceCurrentMatch &&
            !isDesignerSmartGuildReactivationState;
          const smartGuildFeeFeatures = designerSmartGuildSplit
            ? isDesignerSmartGuildReactivationState
              ? [
                  `Annual Marketing Fee: ${formatWholeDollarCurrency(designerSmartGuildSplit.annualFeeAmount)} PAID`,
                  "TURN MEMBERSHIP BACK ON",
                  `${designerSmartGuildRemainingMonths} ${designerSmartGuildRemainingMonths === 1 ? "MONTH" : "MONTHS"} REMAINING`,
                  `Monthly Platform Fee: ${formatCurrency(designerSmartGuildReactivationDueNowAmount)}`,
                  `Due Now: You have ${designerSmartGuildRemainingMonths} ${designerSmartGuildRemainingMonths === 1 ? "month" : "months"} remaining in your marketing fee. You may turn your subscription back on at any time to take advantage of the marketing fee you already paid.`,
                ]
              : isPaidDesignerCurrentPlan
                ? [
                    `Annual Marketing Fee: ${formatWholeDollarCurrency(designerSmartGuildSplit.annualFeeAmount)} PAID`,
                    `Monthly Platform Fee: ${formatCurrency(designerSmartGuildSplit.monthlyFeeAmount)}`,
                    nextMonthlyPlatformFeeDateLabel
                      ? `Your next monthly platform fee will be billed to the payment form on file on ${nextMonthlyPlatformFeeDateLabel}.`
                      : "Your next monthly platform fee will be billed to the payment form on file.",
                  ]
                : [
                    `Annual Marketing Fee: ${formatCurrency(designerSmartGuildSplit.annualFeeAmount)}`,
                    `Monthly Platform Fee: ${formatCurrency(designerSmartGuildSplit.monthlyFeeAmount)}`,
                    `Due Now: ${formatCurrency(designerSmartGuildSplit.dueNowAmount)}`,
                  ]
            : [];
          const isResubscribeAction =
            hasCancellationScheduled &&
            isTierCadenceCurrentMatch &&
            actionType === "upgrade" &&
            (isHomeownerAccount || designerCurrentPlanButtonState === "reactivate");
          const isCurrentPlan = actionType === "current";
          const actionLabel = isCurrentPlan
            ? "Current Plan"
            : isDesignerSmartGuildReactivationState
              ? "REACTIVE"
            : isDesignerSmartGuildCancelledPurchaseState
              ? "PURCHASE"
            : isResubscribeAction
              ? isHomeownerAccount
                ? "Re-Subscribe"
                : "REACTIVE"
            : actionType === "upgrade"
              ? isHomeownerAccount && tier === "Guild+"
                ? "Upgrade to Match"
                : isHomeownerAccount
                  ? "Upgrade"
                  : "PURCHASE"
              : "Downgrade";

          return (
            <Box key={tier} className="my-account-plan-card-stack">
              <SubscriptionCard
                title={planTitle}
                features={planFeatures}
                plainFeatures={smartGuildFeeFeatures}
                description={planDescription}
                paymentFrequency={getPaymentFrequencyFromCadence(cardCadence)}
                price={displayedPlanPrice}
                originalPrice={planOriginalPrice ?? undefined}
                selected={isCurrentPlan || isTierCadenceCurrentMatch}
                isBeta={isBeta}
                selectable={false}
                className="my-account-subscription-card"
              />
              <Button
                className={
                  isCurrentPlan
                    ? "my-account-plan-btn my-account-plan-btn--current"
                    : "my-account-plan-btn"
                }
                variant={isCurrentPlan ? "contained" : "outlined"}
                onClick={() => handlePlanCardAction(tier)}
                disabled={isCurrentPlan || isMembershipUpdateLoading}
              >
                {actionLabel}
              </Button>
            </Box>
          );
        })}
      </Box>

      {!isHomeownerAccount && (
        <Paper
          className="my-account-cancellation-panel"
          variant="outlined"
          sx={{ mt: 2 }}
        >
          <Box>
            <Typography className="my-account-form-block-title">
              Add-On Offer
            </Typography>
            <Typography className="my-account-muted-copy">
              Homeowner Financing — Powered by Enhancify
            </Typography>
          </Box>

          <Button
            variant="outlined"
            href={ENHANCIFY_PROMO_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="my-account-primary-btn"
            sx={{
              color: "#111827",
              borderColor: "rgba(17,24,39,.25)",
              "&:hover": {
                borderColor: "#111827",
                backgroundColor: "rgba(17,24,39,.04)",
              },
            }}
          >
            Add-On
          </Button>
        </Paper>
      )}

      <Paper className="my-account-cancellation-panel" variant="outlined">
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography className="my-account-form-block-title">Cancellation</Typography>
          <Stack direction="row" spacing={0.75} alignItems="center">
            <Typography className="my-account-muted-copy">Cancel plan</Typography>
            <Tooltip
              arrow
              placement="top-start"
              enterTouchDelay={0}
              leaveTouchDelay={4000}
              title={
                <Box sx={{ maxWidth: 320, py: 0.5 }}>
                  <Typography sx={{ fontSize: 12.5, lineHeight: 1.5, color: "#fff" }}>
                    {cancellationModalMessage}
                  </Typography>
                </Box>
              }
            >
              <IconButton
                size="small"
                aria-label="View cancellation policy"
                className="my-account-policy-trigger"
              >
                <InfoOutlined fontSize="small" />
              </IconButton>
            </Tooltip>
          </Stack>
        </Box>

        <Button
          variant="contained"
          className="my-account-primary-btn"
          disabled={
            isMembershipUpdateLoading ||
            hasCancellationScheduled ||
            (!isHomeownerAccount &&
              (hasDesignerSmartGuildCancelledState || !hasActiveDesignerMembership))
          }
          onClick={() => setCancelDialogOpen(true)}
        >
          Cancel
        </Button>
      </Paper>
    </Box>
  );

  if (loading) {
    return (
      <Box className="my-account-page my-account-page--loading">
        <CircularProgress sx={{ color: "#4f46e5" }} />
      </Box>
    );
  }

  if (!user) {
    return null;
  }

  return (
    <Box className="my-account-page">
      <Typography variant="h4" className="my-account-page-title">
        Account Settings
      </Typography>

      <Paper className="my-account-shell" variant="outlined">
        <Tabs value={activeTab} onChange={handleTabChange} className="my-account-tabs">
          <Tab
            value="profile"
            icon={<PersonOutlineOutlined fontSize="small" />}
            iconPosition="start"
            label="My Profile"
          />
          <Tab
            value="billing"
            icon={<CreditCardOutlined fontSize="small" />}
            iconPosition="start"
            label="Billing"
          />
          <Tab
            value="subscription"
            icon={<DescriptionOutlined fontSize="small" />}
            iconPosition="start"
            label="Subscription Plan"
            disabled={!isDesignerApplicationCompleted}
          />
        </Tabs>

        <Box className="my-account-content">
          {activeTab === "profile" && profileTabContent}

          {activeTab === "billing" && billingTabContent}

          {activeTab === "subscription" && subscriptionTabContent}
        </Box>
      </Paper>

      <Dialog open={upgradeDialogOpen} onClose={() => setUpgradeDialogOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>
          {upgradeDialogTitle}
          <IconButton
            size="small"
            onClick={() => setUpgradeDialogOpen(false)}
            className="my-account-dialog-close"
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Typography variant="h6" sx={{ mb: 1 }}>
            {pendingPlanTier}er Membership
          </Typography>
          <Typography className="my-account-muted-copy" sx={{ mb: 3 }}>
            {!isHomeownerAccount
              ? isPendingDesignerSmartGuildReactivation
                ? `Your ${formatWholeDollarCurrency(DESIGNER_SMART_GUILD_ANNUAL_FEE_DEFAULT)} marketing fee remains active for ${designerSmartGuildRemainingMonths} ${designerSmartGuildRemainingMonths === 1 ? "month" : "months"}. Turning your membership back on will resume the monthly platform fee without restarting your annual term.`
                : `Your annual membership will start now. ${designerCancellationPolicyMessage} Your full subscription will auto-renew in 12 months. Auto-renew can be turned off at any time.`
              : "Your subscription will start now."}
          </Typography>

          <Typography variant="h5" sx={{ mb: 2 }}>
            DUE NOW: {formatCurrency(isHomeownerAccount ? activePlanPrice : pendingPlanDueNowPrice)}
          </Typography>
          {!isHomeownerAccount && (
            <Typography className="my-account-muted-copy" sx={{ mb: 2 }}>
              {isPendingDesignerSmartGuildReactivation
                ? `- You have ${designerSmartGuildRemainingMonths} ${designerSmartGuildRemainingMonths === 1 ? "month" : "months"} remaining in your paid marketing fee.`
                : `- Recurring Monthly Platform Fee: ${formatCurrency(
                    pendingDesignerSmartGuildSplit?.monthlyFeeAmount ?? 100
                  )}`}
            </Typography>
          )}

          <FormControlLabel
            control={
              <Checkbox
                checked={upgradeConsentChecked}
                onChange={(event) => setUpgradeConsentChecked(event.target.checked)}
                size="small"
              />
            }
            label="You agree Home Design Guild can charge your card for this recurring membership."
          />

          <Typography className="my-account-modal-policy" sx={{ mt: 2 }}>
            By submitting this review, you agree to our{" "}
            <a href={TERMS_OF_SERVICE_URL} target="_blank" rel="noopener noreferrer">
              Terms
            </a>{" "}
            and{" "}
            <a href={PRIVACY_POLICY_URL} target="_blank" rel="noopener noreferrer">
              Privacy Policy
            </a>
            .
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              void handleUpgradeConfirm();
            }}
            variant="contained"
            className="my-account-primary-btn"
            disabled={isMembershipUpdateLoading}
          >
            {isMembershipUpdateLoading ? "Updating..." : upgradeDialogPrimaryActionLabel}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={downgradeDialogOpen}
        onClose={() => setDowngradeDialogOpen(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>
          Downgrade Subscription Plan
          <IconButton
            size="small"
            onClick={() => setDowngradeDialogOpen(false)}
            className="my-account-dialog-close"
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Typography variant="h6" sx={{ mb: 1 }}>
            {pendingPlanTier} Plan
          </Typography>
          <Typography className="my-account-muted-copy" sx={{ mb: 2 }}>
            If you switch plans, your current plan remains active until the end of your current
            billing period.
          </Typography>

          <Typography variant="h5" sx={{ mb: 2 }}>
            {formatCurrency(activePlanPrice)}
          </Typography>

          <Typography className="my-account-modal-policy">
            By submitting this review, you agree to our{" "}
            <a href={TERMS_OF_SERVICE_URL} target="_blank" rel="noopener noreferrer">
              Terms
            </a>{" "}
            and{" "}
            <a href={PRIVACY_POLICY_URL} target="_blank" rel="noopener noreferrer">
              Privacy Policy
            </a>
            .
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleDowngradeConfirm}
            variant="contained"
            className="my-account-primary-btn"
            disabled={isMembershipUpdateLoading}
          >
            {isMembershipUpdateLoading ? "Updating..." : "Confirm Downgrade"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={cancelDialogOpen} onClose={() => setCancelDialogOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>
          Confirm Cancellation
          <IconButton
            size="small"
            onClick={() => setCancelDialogOpen(false)}
            className="my-account-dialog-close"
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Typography variant="h6">{currentPlanTier}er Membership</Typography>
          <Typography className="my-account-muted-copy" sx={{ my: 2 }}>
            {cancellationModalMessage}
          </Typography>
          <Typography className="my-account-modal-policy" sx={{ mt: 2 }}>
            By submitting this review, you agree to our{" "}
            <a href={TERMS_OF_SERVICE_URL} target="_blank" rel="noopener noreferrer">
              Terms
            </a>{" "}
            and{" "}
            <a href={PRIVACY_POLICY_URL} target="_blank" rel="noopener noreferrer">
              Privacy Policy
            </a>
            .
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleConfirmCancellation}
            variant="contained"
            className="my-account-primary-btn"
            disabled={isMembershipUpdateLoading}
          >
            {isMembershipUpdateLoading ? "Updating..." : "Cancel Membership"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={feedbackDialogOpen}
        onClose={() => setFeedbackDialogOpen(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>
          Subscription has been cancelled
          <IconButton
            size="small"
            onClick={() => setFeedbackDialogOpen(false)}
            className="my-account-dialog-close"
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Typography variant="h6" sx={{ mb: 2 }}>
            We would love your feedback - tell us why you cancelled this subscription.
          </Typography>

          <Box className="my-account-feedback-reasons">
            {CANCELLATION_REASONS.map((reason) => (
              <FormControlLabel
                key={reason}
                control={
                  <Checkbox
                    checked={feedbackReasons.includes(reason)}
                    onChange={() => handleFeedbackReasonToggle(reason)}
                    size="small"
                  />
                }
                label={reason}
              />
            ))}
          </Box>

          <TextField
            label="Additional feedback"
            multiline
            minRows={4}
            value={feedbackNotes}
            onChange={(event) => setFeedbackNotes(event.target.value)}
            inputProps={{ maxLength: 500 }}
            fullWidth
            sx={{ mt: 1 }}
            helperText={`${feedbackNotes.length}/500`}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setFeedbackDialogOpen(false)}>No Thank You</Button>
          <Button
            onClick={handleFeedbackSubmit}
            variant="contained"
            className="my-account-primary-btn"
          >
            Submit Feedback
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={addPaymentMethodPromptOpen}
        onClose={() => setAddPaymentMethodPromptOpen(false)}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>
          Add Payment Method
          <IconButton
            size="small"
            onClick={() => setAddPaymentMethodPromptOpen(false)}
            className="my-account-dialog-close"
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Typography>
            You do not have a credit card added yet. Add a payment method to continue.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddPaymentMethodPromptOpen(false)}>Not Now</Button>
          <Button
            onClick={handleGoToAddPaymentMethod}
            variant="contained"
            className="my-account-primary-btn"
          >
            Go to Add Payment Method
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
